import {
  Cpu,
  Users,
  Compass,
  Microscope,
  Database,
  CheckCircle2,
  Server,
  PanelsTopLeft,
  type LucideIcon,
} from 'lucide-react';
import { personaBySlug, type PersonaSlug } from '@/lib/personas';
import { cn } from '@/lib/utils';

const ICON_MAP: Record<string, LucideIcon> = {
  Cpu,
  Users,
  Compass,
  Microscope,
  Database,
  CheckCircle2,
  Server,
  PanelsTopLeft,
};

export interface PersonaFilterChipProps {
  persona: PersonaSlug;
  selected: boolean;
  onToggle: () => void;
  className?: string;
}

export function PersonaFilterChip({
  persona,
  selected,
  onToggle,
  className,
}: PersonaFilterChipProps) {
  const p = personaBySlug[persona];
  const Icon = ICON_MAP[p.icon] ?? Cpu;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={`Filter by ${p.name}`}
      onClick={onToggle}
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 rounded-full border pl-2.5 pr-3 font-sans',
        'transition-[border-color,background-color,color] duration-150 ease-out',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:[outline-color:hsl(var(--accent-ring))]',
        selected
          ? 'border-rule-strong bg-bg-mute text-ink shadow-[inset_0_1px_0_hsl(var(--rule)/0.6)]'
          : 'border-rule bg-bg-elev text-ink-soft hover:border-rule-strong',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-3.5 w-[4px] shrink-0 rounded-sm',
          selected ? 'bg-ink-soft' : 'bg-rule-strong',
        )}
      />
      <Icon
        size={14}
        strokeWidth={1.75}
        aria-hidden="true"
        className="text-ink-mute shrink-0"
      />
      <span
        className="whitespace-nowrap text-[13px] font-medium"
        style={{ letterSpacing: '-0.005em' }}
      >
        {p.shortName}
      </span>
    </button>
  );
}

export default PersonaFilterChip;
